import axios from 'axios';
import { getToken } from './authService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

// 10 min para soportar agentes deep (theory_evaluator, entity_researcher
// auto-run completo). nginx en Dokku también está en 600s, así matchean.
const client = axios.create({ baseURL: API_BASE, timeout: 600000 });
client.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// Investigations top-level
export async function listInvestigations(status) {
  const { data } = await client.get('/investigations', { params: status ? { status } : undefined });
  return data.investigations || [];
}

export async function createInvestigation(payload) {
  const { data } = await client.post('/investigations', payload);
  return data.investigation;
}

export async function getInvestigation(idOrSlug) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}`);
  return data;
}

export async function updateInvestigation(idOrSlug, patch) {
  const { data } = await client.patch(`/investigations/${encodeURIComponent(idOrSlug)}`, patch);
  return data.investigation;
}

export async function deleteInvestigation(idOrSlug) {
  await client.delete(`/investigations/${encodeURIComponent(idOrSlug)}`);
}

// Sub-recursos
async function listResource(idOrSlug, resource) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/${resource}`);
  return data[resource] || [];
}

async function createResource(idOrSlug, resource, payload) {
  const { data } = await client.post(`/investigations/${encodeURIComponent(idOrSlug)}/${resource}`, payload);
  return data.item;
}

async function deleteResource(idOrSlug, resource, itemId) {
  await client.delete(`/investigations/${encodeURIComponent(idOrSlug)}/${resource}`, {
    params: { item: itemId },
  });
}

async function patchResource(idOrSlug, resource, itemId, patch) {
  const { data } = await client.patch(
    `/investigations/${encodeURIComponent(idOrSlug)}/${resource}`,
    patch,
    { params: { item: itemId } }
  );
  return data.item;
}

async function getResourceItem(idOrSlug, resource, itemId) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/${resource}`, {
    params: { item: itemId },
  });
  return data.item;
}

// Entities
export const listEntities = (id) => listResource(id, 'entities');
export const createEntity = (id, payload) => createResource(id, 'entities', payload);
export const updateEntity = (id, eid, patch) => patchResource(id, 'entities', eid, patch);
export const deleteEntity = (id, eid) => deleteResource(id, 'entities', eid);

// Connections
export const listConnections = (id) => listResource(id, 'connections');
export const createConnection = (id, payload) => createResource(id, 'connections', payload);
export const deleteConnection = (id, cid) => deleteResource(id, 'connections', cid);

// Reports
export const listReports = (id) => listResource(id, 'reports');
export const getReport = (id, rid) => getResourceItem(id, 'reports', rid);
export const createReport = (id, payload) => createResource(id, 'reports', payload);
export const deleteReport = (id, rid) => deleteResource(id, 'reports', rid);

// Theories
export const listTheories = (id) => listResource(id, 'theories');
export const createTheory = (id, payload) => createResource(id, 'theories', payload);
export const getTheory = (id, tid) => getResourceItem(id, 'theories', tid);
export const updateTheory = (id, tid, patch) => patchResource(id, 'theories', tid, patch);
export const deleteTheory = (id, tid) => deleteResource(id, 'theories', tid);

// Notes
export const listNotes = (id) => listResource(id, 'notes');
export const createNote = (id, payload) => createResource(id, 'notes', payload);
export const deleteNote = (id, nid) => deleteResource(id, 'notes', nid);

// Searches (log)
export const listSearches = (id) => listResource(id, 'searches');

// Agentes IA
export async function listAgents(idOrSlug) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/run-agent`);
  return data.agents || [];
}

export async function runAgent(idOrSlug, agent, input) {
  const { data } = await client.post(`/investigations/${encodeURIComponent(idOrSlug)}/run-agent`, {
    agent,
    input,
  });
  return data;
}

/** Wizard conversacional para crear nuevas investigaciones. */
export async function wizardChat({ messages = [], confirm = false, proposal = null } = {}) {
  const { data } = await client.post('/investigations/wizard', { messages, confirm, proposal });
  return data;
}

/** Estado del auto-run (activo + plan + runs históricos + loop). */
export async function getAutoRunStatus(idOrSlug) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/auto-run`);
  return data;
}

/** Cambia el estado del loop continuo. */
export async function setLoopState(idOrSlug, action, value) {
  const { data } = await client.patch(
    `/investigations/${encodeURIComponent(idOrSlug)}/loop`,
    { action, value }
  );
  return data;
}

/** Dispara el orquestador completo (los 5 agentes en secuencia). */
export async function launchAutoRun(idOrSlug, { force = false } = {}) {
  const { data } = await client.post(`/investigations/${encodeURIComponent(idOrSlug)}/auto-run`, { force });
  return data;
}

/** Detalles de un run específico (incluye searches paso por paso). */
export async function getAutoRunById(idOrSlug, runId) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/auto-run`, {
    params: { run: runId },
  });
  return data;
}

/** Chat persistido del dossier. */
export async function getDossierChatHistory(idOrSlug) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/dossier-chat-history`);
  return data.messages || [];
}

export async function clearDossierChatHistory(idOrSlug) {
  await client.delete(`/investigations/${encodeURIComponent(idOrSlug)}/dossier-chat-history`);
}

/** Mandar mensaje al chat del dossier (con tools). */
export async function sendDossierChat(idOrSlug, question) {
  const { data } = await client.post(`/investigations/${encodeURIComponent(idOrSlug)}/dossier-chat`, { question });
  return data;
}

/** Historial de chat scoped a una teoría. */
export async function getTheoryChatHistory(idOrSlug, theoryId) {
  const { data } = await client.get(`/investigations/${encodeURIComponent(idOrSlug)}/theory-chat`, { params: { tid: theoryId } });
  return data.messages || [];
}

/** Enviar mensaje al chat de una teoría. Devuelve answer + theory actualizada. */
export async function sendTheoryChat(idOrSlug, theoryId, question) {
  const { data } = await client.post(
    `/investigations/${encodeURIComponent(idOrSlug)}/theory-chat`,
    { question },
    { params: { tid: theoryId } }
  );
  return data;
}

export async function clearTheoryChat(idOrSlug, theoryId) {
  await client.delete(`/investigations/${encodeURIComponent(idOrSlug)}/theory-chat`, { params: { tid: theoryId } });
}

/** Vista cross-dossier de la red de SN: entidades + conexiones agregadas. */
export async function getGlobalNetwork() {
  const { data } = await client.get('/investigations/network');
  return data;
}

export async function getNetworkEntity(normSlug) {
  const { data } = await client.get('/investigations/network', { params: { entity: normSlug } });
  return data.entity;
}

/* ============ POLÍTICOS NICOLEÑOS (HCD + dossiers unificados) ============ */

export async function listPoliticians() {
  const { data } = await client.get('/politicians');
  return data.politicians || [];
}

export async function getPolitician(slug) {
  const { data } = await client.get('/politicians', { params: { slug } });
  return data.politician;
}

export async function getPoliticianChatHistory(slug) {
  const { data } = await client.get('/politicians/chat', { params: { slug } });
  return data.messages || [];
}

export async function sendPoliticianChat(slug, question) {
  const { data } = await client.post('/politicians/chat', { question }, { params: { slug } });
  return data;
}

export async function clearPoliticianChatHistory(slug) {
  await client.delete('/politicians/chat', { params: { slug } });
}

/** Síntesis de inteligencia GUARDADA (la genera el cron diario). GET, sin LLM. */
export async function getPoliticianSynthesis(slug) {
  const { data } = await client.get('/politicians/synthesis', { params: { slug } });
  return data;
}

/** Regenera AHORA la síntesis (RAG + LLM) y la guarda. POST. */
export async function generatePoliticianSynthesis(slug) {
  const { data } = await client.post('/politicians/synthesis', null, { params: { slug } });
  return data;
}

const investigationService = {
  listInvestigations, createInvestigation, getInvestigation, updateInvestigation, deleteInvestigation,
  listEntities, createEntity, updateEntity, deleteEntity,
  listConnections, createConnection, deleteConnection,
  listReports, getReport, createReport, deleteReport,
  listTheories, createTheory, deleteTheory,
  listNotes, createNote, deleteNote,
  listSearches,
};
export default investigationService;
